import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  IconButton,
  Box,
} from '@mui/material';
import { Close, Save } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { FileUploader } from '../../../../components/file/file-uploader';
import { Loader } from '../../../../components';
import { ConfirmationDialogue } from '../../../../components/modal/ConfirmationDialogue';
import { IAgentDrawsAndDeductionsAPI, ImportAgentDrawsResult } from '../../../../fetch/agent-draws-and-deductions';

const Schema = Yup.object().shape({
  importName: Yup.string().required('Required'),
  fileName: Yup.string().required('Required'),
});

interface ImportDrawsModalProps {
  open: boolean;
  onClose: () => void;
  onImportSuccess: (result: ImportAgentDrawsResult) => void;
}

const ImportDrawsModal: React.FC<ImportDrawsModalProps> = ({ open, onClose, onImportSuccess }) => {
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [errorsList, setErrorsList] = useState<string[] | null>(null);
  const [preview, setPreview] = useState<{ recordCount: number | undefined; emailRecipientCount: number | undefined } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const handleCancel = () => {
    setUploadedFile(null);
    setErrorsList(null);
    setPreview(null);
    setPreviewError(null);
    setPreviewLoading(false);
    onClose();
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedRunPreview = (importName: string, file: File | null) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      runPreview(importName, file);
    }, 500);
  };

  const runPreview = async (importName: string, file: File | null) => {
    if (!file) {
      setPreview(null);
      setPreviewError(null);
      return;
    }

    if (!importName.trim()) {
      setPreview(null);
      setPreviewError('Import name is required.');
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    setPreview(null);

    try {
      const result = await IAgentDrawsAndDeductionsAPI.previewImportDraws(importName, file);

      if (result.validationErrors?.length) {
        setPreview(null);
        setPreviewError(result.validationErrors.join(' '));
      } else if (result.errors?.length) {
        setPreview(null);
        setPreviewError(result.errors.join(' '));
      } else {
        setPreview({ recordCount: result.recordCount ?? 0, emailRecipientCount: result.emailRecipientCount ?? 0 });
      }
    } catch {
      setPreview(null);
      setPreviewError('Could not read this file. Please check the format and try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullWidth
      maxWidth="sm"
      sx={{
        '& .MuiDialog-paper': {
          width: '720px',
          maxWidth: 'calc(100% - 48px)',
        },
      }}
    >
      <Formik
        enableReinitialize
        initialValues={{ importName: '', fileName: '' }}
        validationSchema={Schema}
        onSubmit={async (values, actions) => {
          try {
            const res = await IAgentDrawsAndDeductionsAPI.importDraws(values.importName, uploadedFile);

            if (res?.Detail || res?.detail) {
              setErrorsList((res.Detail || res.detail).split('\r\n'));
              setPreview(null);
              setPreviewError(null);
              return;
            } else if (res?.errors?.length > 0 || res?.validationErrors?.length > 0) {
              setErrorsList(
                (res.validationErrors || []).concat(res.errors || []).map((error: string) => `${error}\r\n`)
              );
              setPreview(null);
              setPreviewError(null);
              return;
            }

            // Success — reset form, close modal, hand off to parent
            actions.resetForm();
            setUploadedFile(null);
            setPreview(null);
            setPreviewError(null);
            setPreviewLoading(false);
            onClose();
            onImportSuccess(res);
          } catch (error: any) {
            setPreview(null);
            setPreviewError(null);
            if (error?.errors && Array.isArray(error.errors)) {
              setErrorsList(error.errors);
            } else {
              setErrorsList(['Error importing draws, please try again.']);
            }
          }
        }}
      >
        {({ isSubmitting, values, setFieldValue, errors, handleSubmit, dirty, isValid, handleBlur }) => (
          <Form onSubmit={handleSubmit} autoComplete="none">
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 0.5,
                px: 1.5,
                bgcolor: '#005570',
                color: '#eff4f6',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                Import Draws
              </Typography>
              <IconButton onClick={handleCancel} size="small" sx={{ color: '#eff4f6' }} disabled={isSubmitting}>
                <Close fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 2, pt: 3, pb: 1, overflowX: 'hidden' }}>
              <Box sx={{ mt: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  autoComplete="nope"
                  label="Import Name"
                  placeholder="Enter an import name (e.g., January 2026 Draws)"
                  name="importName"
                  value={values.importName}
                  onBlur={handleBlur}
                  size="small"
                  required
                  onChange={e => {
                    const newImportName = e.target.value;
                    setFieldValue('importName', newImportName);
                    if (uploadedFile && newImportName.trim()) {
                      debouncedRunPreview(newImportName, uploadedFile);
                    } else if (uploadedFile && !newImportName.trim()) {
                      setPreview(null);
                      setPreviewError(null);
                    }
                  }}
                  error={!!errors?.importName}
                  helperText={errors?.importName}
                  inputProps={{ maxLength: 300 }}
                  disabled={isSubmitting}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <FileUploader
                  handleFile={val => {
                    setUploadedFile(val);
                    setFieldValue('fileName', val?.name ?? '');
                    runPreview(values.importName, val);
                  }}
                  handleRemove={() => {
                    setUploadedFile(null);
                    setFieldValue('fileName', '');
                    setPreview(null);
                    setPreviewError(null);
                  }}
                  removeText="Remove"
                  templateFile={values.fileName}
                  isRequired={true}
                  dropzoneText="Drag and drop your import file here, or click to browse (.xlsx)"
                />
              </Box>
              {previewLoading && (
                <Box sx={{ p: 1.5, mb: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Reading file…
                  </Typography>
                </Box>
              )}

              {!previewLoading && previewError && (
                <Box
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: '#fdecea',
                    border: '1px solid #f5c6cb',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle2" color="error" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Import Error
                  </Typography>
                  <Typography variant="body2" color="error">
                    {previewError}
                  </Typography>
                </Box>
              )}

              {!previewLoading && !previewError && preview && (
                <Box
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: '#eef6f9',
                    border: '1px solid #cfe3ea',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Import Summary
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1 }}>
                    <Typography component="li" variant="body2">
                      Records to be added: <strong>{preview.recordCount}</strong>
                    </Typography>
                    <Typography component="li" variant="body2">
                      Users who will receive email: <strong>{preview.emailRecipientCount}</strong>
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Review these totals before importing. Once imported, this action cannot be undone.
                  </Typography>
                </Box>
              )}
              <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Expected Excel columns:</strong> FrankieID, Amount, Memo, Date, Notify Line Lead (optional)
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2, justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={handleCancel} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Save />}
                type="submit"
                disabled={!dirty || isSubmitting || !isValid || previewLoading || !!previewError || !preview || preview.recordCount === 0}
              >
                Import
              </Button>
            </DialogActions>
            {errorsList && (
              <ConfirmationDialogue
                open
                id="import-draw-errors"
                title="Import Errors"
                text={errorsList.join('\r\n')}
                hideCancel
                onClose={() => {
                  setErrorsList(null);
                  setUploadedFile(null);
                  setFieldValue('fileName', '');
                }}
                onConfirm={() => {
                  setErrorsList(null);
                  setUploadedFile(null);
                  setFieldValue('fileName', '');
                }}
              />
            )}
            {isSubmitting && (
              <Loader position="centered" type="fullscreen">
                Importing draws and sending approval emails...
              </Loader>
            )}
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ImportDrawsModal;
